import {useCallback, useEffect, useSyncExternalStore} from 'react'
import {
  acquireRelay,
  releaseRelay,
  refreshRelaySnapshot,
  subscribeRelayRegistry,
  isRelayActive,
  getRelayStatus,
  getRelayError,
  getRelayStore,
} from '@github-ui/ahp-relay/relay-registry'
import type {RelayStatus} from '@github-ui/ahp-relay/relay-connection-runtime'
import {AhpChatStore} from '@github-ui/ahp-relay/ahp-chat-store'
import {isWorking as selectIsWorking} from '@github-ui/ahp-relay/ahp-session-selectors'
import type {WebSocketFactory} from '@github-ui/ahp-relay/webpubsub/wps-transport'
import type {ChatState} from '@microsoft/agent-host-protocol'
import {mcFetcher} from '../utils/mc-fetcher'

export interface UseAhpSessionStateOptions {
  taskId: string | undefined
  environmentId: string | undefined
  /** Mission Control session id the relay observes. */
  sessionId: string | undefined
  /** Channel URI to observe. Defaults to `ahp-session:/<sessionId>`. */
  channel?: string
  /** Opens the websocket used by the underlying WPS transport. */
  webSocketFactory?: WebSocketFactory
  /** Observes relay status changes for host-app diagnostics. */
  onStatus?: (status: RelayStatus, error?: string) => void
  /** Called after this hook acquires the shared relay. */
  onRelayAcquired?: (taskId: string) => void
  /** When false the relay is inert (no connection, no registry entry). */
  enabled: boolean
}

export interface UseAhpSessionStateResult {
  /** The canonical AHP chat state, or undefined before the first snapshot. */
  state: ChatState | undefined
  /** Whether the agent is actively working (an active turn is present). */
  isWorking: boolean
  active: boolean
  status: RelayStatus | 'idle'
  error?: string
}

/**
 * Acquires a shared AHP relay and exposes its live `ChatState`.
 */
export function useAhpSessionState({
  taskId,
  environmentId,
  sessionId,
  channel,
  webSocketFactory,
  onStatus,
  onRelayAcquired,
  enabled,
}: UseAhpSessionStateOptions): UseAhpSessionStateResult {
  const shouldConnect = enabled && !!taskId && !!environmentId && !!sessionId

  useEffect(() => {
    if (!shouldConnect || !taskId || !environmentId || !sessionId) return
    const wasActiveBefore = isRelayActive(taskId)
    void acquireRelay({
      taskId,
      environmentId,
      sessionId,
      ...(channel ? {channel} : {}),
      ...(webSocketFactory ? {webSocketFactory} : {}),
      ...(onStatus ? {onStatus} : {}),
      fetcher: mcFetcher,
      createSink: () => new AhpChatStore(),
    })
    // Re-attaching to a relay another consumer already runs can leave the store
    // stale if their consumer was torn down mid-stream; pull a fresh snapshot.
    if (wasActiveBefore) {
      refreshRelaySnapshot(taskId)
    }
    onRelayAcquired?.(taskId)
    return () => releaseRelay(taskId)
  }, [shouldConnect, taskId, environmentId, sessionId, channel, webSocketFactory, onStatus, onRelayAcquired])

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      let current = getRelayStore(taskId)
      let storeUnsubscribe = current?.subscribe(onStoreChange)
      const registryUnsubscribe = subscribeRelayRegistry(() => {
        const next = getRelayStore(taskId)
        if (next !== current) {
          // The store instance was replaced (new acquire cycle): re-attach to it
          // and notify so the fresh snapshot is read.
          storeUnsubscribe?.()
          current = next
          storeUnsubscribe = current?.subscribe(onStoreChange)
          onStoreChange()
        }
      })
      return () => {
        registryUnsubscribe()
        storeUnsubscribe?.()
      }
    },
    [taskId],
  )

  const state = useSyncExternalStore(
    subscribe,
    () => getRelayStore(taskId)?.getSnapshot(),
    () => undefined,
  )

  const active = useSyncExternalStore(
    subscribeRelayRegistry,
    () => isRelayActive(taskId),
    () => false,
  )
  const status = useSyncExternalStore(
    subscribeRelayRegistry,
    () => getRelayStatus(taskId),
    () => 'idle' as const,
  )
  const error = useSyncExternalStore(
    subscribeRelayRegistry,
    () => getRelayError(taskId),
    () => undefined,
  )

  return {
    state,
    isWorking: state ? selectIsWorking(state) : false,
    active,
    status,
    error,
  }
}
